import { motion } from 'framer-motion';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ExpertAvatar from '@components/ExpertAvatar';
import styles from '@components/expertSection/schedule/ScheduleSession.module.css';
import Icon from '@components/text/Icon';
import BodyCopy from '@components/typography/BodyCopy';
import Heading4 from '@components/typography/Heading4';
import { GetJourneyWithExpertAndConsultsQuery } from '@typing/Generated';

type Journey = GetJourneyWithExpertAndConsultsQuery['journey'];

export enum ExpertSelectType {
  ANY = 'ANY',
  PRIMARY = 'PRIMARY'
}

type Props = {
  expertType: ExpertSelectType;
  journey: Journey;
  setExpertType: (type: ExpertSelectType) => void;
};

const ExpertTypeSelect = ({ expertType, journey, setExpertType }: Props) => {
  const [expertTypeHover, setExpertSelectTypeHover] = useState<ExpertSelectType | null>(null);

  const primaryExpertSelected = expertType === ExpertSelectType.PRIMARY && expertTypeHover !== ExpertSelectType.ANY;
  const anyExpertSelected = expertType === ExpertSelectType.ANY && expertTypeHover !== ExpertSelectType.PRIMARY;

  const handleExpertSelectTypeHover = (hoverType: ExpertSelectType) => () => {
    if (hoverType === ExpertSelectType.ANY && expertType === ExpertSelectType.PRIMARY) {
      setExpertSelectTypeHover(ExpertSelectType.ANY);
    } else if (hoverType === ExpertSelectType.PRIMARY && expertType === ExpertSelectType.ANY) {
      setExpertSelectTypeHover(ExpertSelectType.PRIMARY);
    }
  };

  return (
    <div className={styles.expertChoices}>
      {journey.primaryExpert && (
        <div className={styles.expertChoice}>
          <motion.div
            animate={primaryExpertSelected ? 'show' : 'hidden'}
            className={primaryExpertSelected ? styles.circleSelected : styles.circle}
            onClick={() => setExpertType(ExpertSelectType.PRIMARY)}
            onMouseEnter={handleExpertSelectTypeHover(ExpertSelectType.PRIMARY)}
            onMouseLeave={() => setExpertSelectTypeHover(null)}
            transition={{ stiffness: 100, type: 'spring' }}
            variants={{ hidden: { scale: 1 }, show: { scale: 1.1 } }}
            whileHover="show"
          >
            <div>
              <ExpertAvatar className={styles.primaryExpertImg} expert={journey.primaryExpert} />
            </div>
          </motion.div>
          <BodyCopy color={expertType === ExpertSelectType.PRIMARY ? 'primary700' : 'neutral300'}>
            {journey.primaryExpert.firstName}
          </BodyCopy>
          <Heading4 color="neutral500">
            <FormattedMessage id="pages.consults.selectTime.grayceExpert" />
          </Heading4>
        </div>
      )}
      <div className={styles.expertChoice}>
        <motion.div
          animate={anyExpertSelected ? 'show' : 'hidden'}
          className={anyExpertSelected ? styles.circleSelected : styles.circle}
          onClick={() => setExpertType(ExpertSelectType.ANY)}
          onMouseEnter={handleExpertSelectTypeHover(ExpertSelectType.ANY)}
          onMouseLeave={() => setExpertSelectTypeHover(null)}
          transition={{ stiffness: 100, type: 'spring' }}
          variants={{ hidden: { scale: 1 }, show: { scale: 1.1 } }}
          whileHover="show"
        >
          <div className={styles.anyExpertImg}>
            <Icon ariaLabelKey="accessibility.ariaLabels.grayceLogo" className={styles.blankIcon} icon="logoIcon" />
          </div>
        </motion.div>
        <BodyCopy color={expertType === ExpertSelectType.ANY ? 'primary700' : 'neutral300'}>
          <FormattedMessage id="pages.consults.selectTime.anyAvailable" />
        </BodyCopy>
        <Heading4 color="neutral500">
          <FormattedMessage id="pages.consults.selectTime.grayceExpert" />
        </Heading4>
      </div>
    </div>
  );
};

export default ExpertTypeSelect;
